import moment from "moment";
import { runComponents } from "pages/PayRoll/Configuration/StatutoryConfiguration/PF/pfConfigData";

// Payroll > Statutory > PF (screens P4–P6b): helpers shared by the screens.
// Data comes from the PF Members APIs (pfMembersApi.js).

export const MEMBER_STATUS_COLORS = { Member: "#12B76A", "Opted out": "#F0954A", "Not in PF": "#6B7280" };

// ---- Derived values ----

export const ageOn = (dob, on = moment()) => (dob ? moment(on).diff(moment(dob), "years") : null);
export const formatDay = (value) => (value ? moment(value).format("DD MMM YYYY") : "–");
export const formatMonthKey = (key) => (key ? moment(`${key}-01`).format("MMM YYYY") : "–");

export const extraOf = (row) => {
  if (row.status === "Opted out") return { label: "Opt-out", tone: "purple" };
  if (row.full_salary && row.extra_amount) return { label: `Full salary + ₹${Number(row.extra_amount).toLocaleString("en-IN")}`, tone: "purple" };
  if (row.full_salary) return { label: "Full salary", tone: "purple" };
  if (row.extra_amount) return { label: `₹${Number(row.extra_amount).toLocaleString("en-IN")} extra`, tone: "purple" };
  return { label: "", tone: null };
};

// ---- Extra PF effect (P6) ----

// Monthly employee PF, extra and employer share for the given choices,
// worked out with the active version's own formulas (the rates are written
// in the contribution formulas; there are no separate rate settings).
export const pfAmounts = (wage, { fullSalary, extra }, settings) => {
  const values = runComponents(
    {
      wage_ceiling: settings.ceiling,
      eps_cap: settings.epsCap,
      eps_stop_age: settings.stopAge,
      pf_basis_default: settings.basis,
      components: settings.components || [],
    },
    {
      BASIC_E: Number(wage) || 0,
      DA_E: 0,
      RETAINING_E: 0,
      TOTAL_EARNINGS: Number(wage) || 0,
      VPF_AMOUNT: Number(extra) || 0,
      EPS_MEMBER: 1,
      AGE: 0,
      ...(fullSalary ? { basis: "ACTUAL" } : {}),
    },
  );
  const amount = (code) => Number(values[code]) || 0;
  const base = amount("EPF_WAGE");
  const employee = amount("EE_PF");
  const employer = amount("ER_EPS") + amount("ER_EPF");
  return { base, employee, extra: Number(extra) || 0, cut: employee + (Number(extra) || 0), employer };
};

// From-month choices: this month and the next five.
export const monthOptions = (count = 6) =>
  Array.from({ length: count }, (_, index) => {
    const month = moment().startOf("month").add(index, "months");
    return { value: month.format("YYYY-MM"), label: month.format("MMM YYYY") };
  });

// ---- Import UAN (P4a–c) ----

// Only Employee ID is required; a blank cell keeps what is saved. Same keys
// and labels as the server template (IMPORT_FIELDS / IMPORT_FIELD_LABELS).
export const UAN_IMPORT_FIELDS = [
  { key: "employee_id", label: "Employee ID", required: true, note: "Yes", match: ["employee id", "emp id", "empid", "employee code", "emp code", "id"] },
  { key: "employee_name", label: "Employee Name", note: "No (for checking)", match: ["employee name", "name", "emp name"] },
  { key: "uan", label: "UAN", note: "No · 12 digits", match: ["uan", "uan number", "uan no", "universal account number"] },
  { key: "pf_joined_on", label: "PF joining date", note: "No · DD/MM/YYYY", match: ["pf joining date", "doj pf", "pf doj", "pf join date", "date of joining pf"] },
  { key: "previous_pf_member", label: "Previous PF member", note: "No · Yes / No", match: ["previous pf member", "old pf", "old pf?", "previous pf"] },
  { key: "eps_member", label: "Pension (EPS) member", note: "No · Yes / No", match: ["pension (eps) member", "pension", "pension?", "eps member", "eps"] },
  { key: "dob", label: "Date of birth", note: "No · DD/MM/YYYY", match: ["date of birth", "dob", "birth date", "d.o.b"] },
  { key: "opt_out_from", label: "Opt out from", note: "No · DOJ or MM/YYYY", match: ["opt out from", "opt-out from", "opt out", "opt-out"] },
  { key: "opt_out_reason", label: "Opt-out reason", note: "With Opt out from", match: ["opt-out reason", "opt out reason", "reason"] },
  { key: "extra_from_month", label: "Extra PF from month", note: "No · MM/YYYY", match: ["extra pf from month", "extra pf from", "vpf from month", "vpf from"] },
  { key: "full_salary", label: "PF on full salary", note: "No · Yes / No", match: ["pf on full salary", "full salary", "full salary pf"] },
  { key: "extra_amount", label: "Extra PF amount", note: "No · whole rupees", match: ["extra pf amount", "extra pf", "vpf amount", "vpf"] },
];

// At least one of these must be mapped, or the import has nothing to save.
const DATA_FIELDS = UAN_IMPORT_FIELDS.filter((field) => !["employee_id", "employee_name"].includes(field.key));

// Header text as matched and saved: trimmed, lower case, single spaces.
export const normalise = (text) => String(text ?? "").trim().toLowerCase().replace(/\s+/g, " ");

export const autoMapUanColumns = (headers) => {
  const mapping = {};
  const used = new Set();
  headers.forEach((header, index) => {
    const field = UAN_IMPORT_FIELDS.find((item) => !used.has(item.key) && item.match.includes(normalise(header)));
    if (field) {
      mapping[index] = field.key;
      used.add(field.key);
    }
  });
  return mapping;
};

export const missingUanFields = (mapping) => {
  const mapped = new Set(Object.values(mapping));
  const missing = UAN_IMPORT_FIELDS.filter((field) => field.required && !mapped.has(field.key));
  if (!DATA_FIELDS.some((field) => mapped.has(field.key))) missing.push({ key: "data", label: "at least one field to import (e.g. UAN or Date of birth)" });
  return missing;
};

// Mapped fields that will be imported (not Employee ID / Name).
export const mappedDataFields = (mapping) => {
  const mapped = new Set(Object.values(mapping));
  return DATA_FIELDS.filter((field) => mapped.has(field.key));
};

// Mapped rows for the preview API: one object per file row with the Cauver
// field keys, raw text (the server checks and converts them).
export const mappedUanRows = (rows, mapping) =>
  rows.map((row) => {
    const item = {};
    Object.entries(mapping).forEach(([index, key]) => {
      item[key] = String(row[index] ?? "").trim();
    });
    return item;
  });

// { "uan no": "uan", ... } - saved per company by the preview and applied
// to the next file before auto-mapping.
export const mappingByHeader = (headers, mapping) =>
  Object.fromEntries(Object.entries(mapping).map(([index, key]) => [normalise(headers[index]), key]));
